import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X, FolderTree, Layers } from "lucide-react";
import { api, BACKEND_URL, getToken } from "@/lib/api";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, AInput, Badge, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

const ROW = { display: "inline-flex", alignItems: "center", gap: 6 };

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${BACKEND_URL}/api/admin/upload`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.detail || "Upload failed");
  return data.url;
}

function IconAction({ icon: Icon, onClick, danger, ...rest }) {
  const { c } = useAdminTheme();
  return (
    <button onClick={onClick} {...rest} style={{ background: "transparent", border: `1px solid ${c.border}`, borderRadius: 8, width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: danger ? c.danger : c.onSurfaceSecondary }}>
      <Icon size={14} />
    </button>
  );
}

function EditModal({ modal, categories, onClose, onSaved }) {
  const { c } = useAdminTheme();
  const [form, setForm] = useState(modal.initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { set("image", await uploadImage(f)); toast.success("Image uploaded"); } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const r = await api(modal.path, { method: modal.method, body: form });
      toast.success(r?.products_moved ? `Saved · ${r.products_moved} product(s) moved to new category` : "Saved");
      onSaved();
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  return (
    <div data-testid="catalog-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="fade-up" style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16, padding: 22, width: "100%", maxWidth: 440, display: "grid", gap: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 className="font-display" style={{ margin: 0, fontSize: 20, color: c.onSurface }}>{modal.title}</h3>
          <IconAction icon={X} onClick={onClose} data-testid="catalog-modal-close" />
        </div>
        <AInput label="Name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="catalog-input-name" autoFocus />
        {modal.kind === "collection" && <AInput label="Tagline" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="catalog-input-tagline" />}
        {modal.kind === "subcategory" && modal.method === "PUT" && (
          <label style={{ display: "block" }}>
            <span style={{ display: "block", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: c.mutedText, marginBottom: 6, fontWeight: 600 }}>Parent category</span>
            <select data-testid="catalog-input-parent" value={form.parent_id} onChange={(e) => set("parent_id", e.target.value)}
              style={{ width: "100%", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: "11px 14px", color: c.onSurface, fontSize: 14, fontFamily: "DM Sans" }}>
              {categories.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            {form.parent_id !== modal.initial.parent_id && <span style={{ fontSize: 12, color: c.warning, marginTop: 6, display: "block" }}>Products in this subcategory will move with it.</span>}
          </label>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 56, height: 56, borderRadius: 10, overflow: "hidden", background: c.surfaceAlt, flexShrink: 0 }}>
            {form.image && <img src={form.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, background: c.primarySoft, color: c.primarySoftText, borderRadius: 10, padding: "9px 14px", fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer" }}>
            <Upload size={14} /> {busy ? "Working…" : "Upload image"}
            <input data-testid="catalog-input-file" type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} disabled={busy} onChange={onFile} />
          </label>
        </div>
        <AInput label="…or image URL" value={form.image || ""} onChange={(e) => set("image", e.target.value)} data-testid="catalog-input-image" />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <AButton variant="ghost" onClick={onClose}>Cancel</AButton>
          <AButton data-testid="catalog-modal-save" onClick={save} disabled={busy}>Save</AButton>
        </div>
      </div>
    </div>
  );
}

function Thumb({ src, size = 40 }) {
  const { c } = useAdminTheme();
  return <div style={{ width: size, height: size, borderRadius: 8, overflow: "hidden", background: c.surfaceAlt, flexShrink: 0 }}>{src && <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}</div>;
}

function CategoryCard({ cat, onEdit, onDelete, onAddSub, onEditSub, onDeleteSub }) {
  const { c } = useAdminTheme();
  return (
    <ACard data-testid={`catalog-category-${cat.id}`} style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${c.divider}` }}>
        <Thumb src={cat.image} size={46} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{cat.name}</div>
          <div style={{ fontSize: 12, color: c.mutedText }}>{cat.product_count} products · {cat.subcategories?.length || 0} subcategories</div>
        </div>
        <AButton variant="soft" style={{ padding: "7px 12px", ...ROW }} onClick={onAddSub} data-testid={`catalog-add-sub-${cat.id}`}><Plus size={13} /> Sub</AButton>
        <IconAction icon={Pencil} onClick={onEdit} data-testid={`catalog-edit-cat-${cat.id}`} />
        <IconAction icon={Trash2} danger onClick={onDelete} data-testid={`catalog-delete-cat-${cat.id}`} />
      </div>
      {(cat.subcategories || []).map((s) => (
        <div key={s.id} data-testid={`catalog-sub-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px 9px 32px", borderBottom: `1px solid ${c.divider}` }}>
          <Thumb src={s.image} size={30} />
          <div style={{ flex: 1, fontSize: 14 }}>{s.name}</div>
          <span style={{ fontSize: 12, color: c.mutedText, marginRight: 6 }}>{s.product_count}</span>
          <IconAction icon={Pencil} onClick={() => onEditSub(s)} data-testid={`catalog-edit-sub-${s.id}`} />
          <IconAction icon={Trash2} danger onClick={() => onDeleteSub(s)} data-testid={`catalog-delete-sub-${s.id}`} />
        </div>
      ))}
    </ACard>
  );
}

function CollectionRow({ col, onEdit, onDelete }) {
  const { c } = useAdminTheme();
  return (
    <div data-testid={`catalog-collection-${col.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${c.divider}` }}>
      <Thumb src={col.image} size={46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{col.name}</div>
        <div className="clamp-1" style={{ fontSize: 12, color: c.mutedText }}>{col.tagline || "—"} · {col.product_count} products</div>
      </div>
      <IconAction icon={Pencil} onClick={onEdit} data-testid={`catalog-edit-col-${col.id}`} />
      <IconAction icon={Trash2} danger onClick={onDelete} data-testid={`catalog-delete-col-${col.id}`} />
    </div>
  );
}

export default function AdminCatalog() {
  const { c } = useAdminTheme();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("categories");
  const [modal, setModal] = useState(null);

  const load = () => api("/admin/taxonomy").then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner full />;

  const remove = async (label, path) => {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try { await api(path, { method: "DELETE" }); toast.success("Deleted"); load(); } catch (e) { toast.error(e.message); }
  };
  const open = (m) => setModal({ ...m, key: Date.now() });
  const tabs = [{ id: "categories", label: "Categories", icon: FolderTree, n: data.categories.length }, { id: "collections", label: "Collections", icon: Layers, n: data.collections.length }];

  return (
    <div className="fade-up">
      <AdminPageTitle title="Catalog" subtitle="Categories, subcategories & collections — synced to web and mobile"
        action={tab === "categories"
          ? <AButton style={ROW} data-testid="catalog-add-category" onClick={() => open({ kind: "category", title: "New category", method: "POST", path: "/admin/categories", initial: { name: "", image: "" } })}><Plus size={14} /> New category</AButton>
          : <AButton style={ROW} data-testid="catalog-add-collection" onClick={() => open({ kind: "collection", title: "New collection", method: "POST", path: "/admin/collections", initial: { name: "", tagline: "", image: "" } })}><Plus size={14} /> New collection</AButton>} />
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {tabs.map((t) => (
          <button key={t.id} data-testid={`catalog-tab-${t.id}`} onClick={() => setTab(t.id)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 999, border: `1px solid ${tab === t.id ? c.primary : c.border}`, cursor: "pointer", background: tab === t.id ? c.primarySoft : "transparent", color: tab === t.id ? c.primarySoftText : c.onSurfaceSecondary, fontWeight: 600, fontSize: 13 }}>
            <t.icon size={14} /> {t.label} <Badge>{t.n}</Badge>
          </button>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: c.mutedText, margin: "0 0 14px" }}>Renaming only changes the display name — products stay linked. Items that still have products can't be deleted.</p>

      {tab === "categories" ? (
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))" }}>
          {data.categories.map((cat) => (
            <CategoryCard key={cat.id} cat={cat}
              onEdit={() => open({ kind: "category", title: "Edit category", method: "PUT", path: `/admin/categories/${cat.id}`, initial: { name: cat.name, image: cat.image || "" } })}
              onDelete={() => remove(cat.name, `/admin/categories/${cat.id}`)}
              onAddSub={() => open({ kind: "subcategory", title: `New subcategory in ${cat.name}`, method: "POST", path: `/admin/categories/${cat.id}/subcategories`, initial: { name: "", image: "" } })}
              onEditSub={(s) => open({ kind: "subcategory", title: "Edit subcategory", method: "PUT", path: `/admin/subcategories/${s.id}`, initial: { name: s.name, image: s.image || "", parent_id: cat.id } })}
              onDeleteSub={(s) => remove(s.name, `/admin/subcategories/${s.id}`)} />
          ))}
        </div>
      ) : (
        <ACard style={{ padding: 0, overflow: "hidden" }}>
          {data.collections.map((col) => (
            <CollectionRow key={col.id} col={col}
              onEdit={() => open({ kind: "collection", title: "Edit collection", method: "PUT", path: `/admin/collections/${col.id}`, initial: { name: col.name, tagline: col.tagline || "", image: col.image || "" } })}
              onDelete={() => remove(col.name, `/admin/collections/${col.id}`)} />
          ))}
        </ACard>
      )}

      {modal && <EditModal key={modal.key} modal={modal} categories={data.categories} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
